import api from './api';
import { EVIDENCE_STATUS } from '../constants/violationConstants';
import { noteRulesShape, useRuleCategorySupport } from './ruleCategorySupport';

/**
 * API-слой модуля «Нарушения» (ТЗ Part 1, раздел 5).
 * baseURL axios-инстанса уже равен /api, поэтому пути здесь относительные.
 * Endpoint'ы Part 2 добавляются функциями в этот же файл, а не внутрь UI.
 */

export const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
  );

export const normalizeListResponse = (data, fallbackLimit = 50) => {
  const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
  const total = Number.isFinite(data?.total) ? data.total : items.length;
  const limit = Number.isFinite(data?.limit) ? data.limit : fallbackLimit;
  const offset = Number.isFinite(data?.offset) ? data.offset : 0;
  return { items, total, limit, offset };
};

export const listViolationEvents = (params = {}) =>
  api
    .get('/violations/events', { params: cleanParams(params) })
    .then((response) => normalizeListResponse(response.data, params.limit));

/**
 * ТЗ V5 №8. GET /analytics/events/unified — нарушения и бизнес-события одним
 * списком: общий total, сортировка и пагинация на backend. Фильтры —
 * массивы (camera_ids, rule_ids, statuses, event_types…), повторяющиеся
 * параметры раскладывает paramsSerializer из api.js.
 */
export const listUnifiedEvents = (params = {}) =>
  api
    .get('/analytics/events/unified', { params: cleanParams(params) })
    .then((response) => ({
      ...normalizeListResponse(response.data, params.limit),
      timezone_mode: response.data?.timezone_mode,
      display_timezone: response.data?.display_timezone,
      display_timezones: response.data?.display_timezones,
    }));

export const getViolationEvent = (eventId) =>
  api.get(`/violations/events/${eventId}`).then((response) => response.data);

export const listViolationRules = (params = {}) =>
  api
    .get('/admin/violation-rules', { params: cleanParams(params) })
    .then((response) => normalizeListResponse(response.data, params.limit));

export const getViolationRule = (ruleId) =>
  api.get(`/admin/violation-rules/${ruleId}`).then((response) => response.data);

export const createViolationRule = (payload) =>
  api.post('/admin/violation-rules', payload).then((response) => response.data);

/** Идемпотентно: для уже отключённого правила backend возвращает его же. */
export const disableViolationRule = (ruleId) =>
  api.post(`/admin/violation-rules/${ruleId}/disable`).then((response) => response.data);

export const getViolationRuleCatalog = (cameraId) =>
  api
    .get('/admin/violation-rule-catalog', { params: { camera_id: cameraId } })
    .then((response) => response.data);

/**
 * Part 2, ТЗ 4.1. expected_schedule_version защищает от перезаписи расписания,
 * изменённого другим администратором: несовпадение версии → 409.
 */
export const updateViolationRuleActivation = (ruleId, payload) =>
  api.put(`/admin/violation-rules/${ruleId}/activation`, payload).then((response) => response.data);

/** Part 2, ТЗ 7.3. Повторная оценка разрешена, audit trail ведёт backend. */
export const updateViolationEventReview = (eventId, payload) =>
  api.put(`/violations/events/${eventId}/review`, payload).then((response) => response.data);

/* ------------------------------------------------------------------ Part 3 */

const RULES_V2 = '/admin/violation-rules-v2';

/**
 * Lifecycle-ответ V2 (Part 4, раздел 3.4) — конверт, а не само правило:
 * `{ rule, outcome, resolved_event_ids }`. Разворачиваем его в одном месте,
 * иначе `outcome` и `resolved_event_ids` попадают в строку таблицы как поля
 * правила, а сами поля правила не обновляются.
 *
 * Ответ без конверта (bare rule) и пустой ответ 204 у DELETE — допустимые формы.
 */
export const unwrapLifecycleResponse = (data) => {
  const body = data && typeof data === 'object' ? data : {};
  const isEnvelope = 'rule' in body || 'outcome' in body || 'resolved_event_ids' in body;

  return {
    rule: isEnvelope ? body.rule ?? null : body.rule_id ? body : null,
    outcome: typeof body.outcome === 'string' ? body.outcome : '',
    resolvedEventIds: Array.isArray(body.resolved_event_ids) ? body.resolved_event_ids : [],
  };
};

/**
 * V2 lifecycle правил (PART 3, раздел 2). V1 `/disable` остаётся в файле как
 * legacy: он умел только отключение, а V2 задаёт полную тройку операций.
 */
export const activateViolationRule = (ruleId) =>
  api.post(`${RULES_V2}/${ruleId}/activate`).then((response) => unwrapLifecycleResponse(response.data));

export const deactivateViolationRule = (ruleId) =>
  api.post(`${RULES_V2}/${ruleId}/deactivate`).then((response) => unwrapLifecycleResponse(response.data));

/** Soft delete / tombstone. Тело ответа не гарантировано (возможен 204). */
export const deleteViolationRule = (ruleId) =>
  api.delete(`${RULES_V2}/${ruleId}`).then((response) => {
    const result = unwrapLifecycleResponse(response.data);
    return { ...result, outcome: result.outcome || 'deleted' };
  });

/* ------------------------------------------------------------------ Part 4 */

/**
 * Управление правилами переезжает на V2 целиком (Part 4, раздел 3.4): список,
 * карточка, создание и retention. V1-функции выше остаются только ради
 * расписания активации, для которого V2-роута нет.
 *
 * include_deleted показывает tombstone'ы: DELETED и INACTIVE — разные состояния.
 */
export const listViolationRulesV2 = (params = {}) =>
  api
    .get(RULES_V2, { params: cleanParams(params) })
    .then((response) => {
      const list = normalizeListResponse(response.data, params.limit);
      // Форма ответа говорит, знает ли backend категории правил (BM v2).
      noteRulesShape(list.items);
      return list;
    });

const probeRuleCategories = () => listViolationRulesV2({ limit: 1 });

/** true, когда backend отдаёт category у правил V2 (ТЗ BM v2, §0). */
export const useRuleCategoriesSupported = () => useRuleCategorySupport(probeRuleCategories);

/**
 * Расписание живёт в отдельном facade GET /admin/violation-rules/{id}/activation
 * и в теле правила V2 не приходит.
 */
export const getViolationRuleActivation = (ruleId) =>
  api.get(`/admin/violation-rules/${ruleId}/activation`).then((response) => response.data);

/**
 * Карточка правила = правило V2 + его activation.
 *
 * Facade расписания выкатывается отдельно от фронта, поэтому его недоступность
 * не должна ломать всю карточку: блок активации presence-driven и просто не
 * рисуется. На чтении это безопасно — расписание, которого мы не получили, мы
 * и не показываем, а PUT расписания при отсутствии facade честно упадёт.
 */
export const getViolationRuleV2 = (ruleId) =>
  Promise.all([
    api.get(`${RULES_V2}/${ruleId}`).then((response) => {
      noteRulesShape(response.data);
      return response.data;
    }),
    getViolationRuleActivation(ruleId).catch(() => null),
  ]).then(([rule, activation]) => (activation ? { ...rule, activation } : rule));

export const createViolationRuleV2 = (payload) =>
  api.post(RULES_V2, payload).then((response) => response.data);

/**
 * Core-поля правила (ТЗ задачи 2–4): PUT /admin/violation-rules-v2/{id},
 * DTO ViolationRuleCoreUpdateV2 (additionalProperties: false). Retention и
 * расписание сюда не входят — у них свои PUT. Backend не активирует правило
 * при сохранении; фронт тоже не должен.
 */
export const updateViolationRuleCore = (ruleId, payload) =>
  api.put(`${RULES_V2}/${ruleId}`, payload).then((response) => response.data);

/**
 * BM-FE-TZ-002C: перевод правила между вкладками «Нарушения» и
 * «Бизнес-метрики». Отдельный PATCH, не PUT core-полей: правило не нужно
 * выключать заранее, повтор с той же категорией идемпотентен.
 */
export const updateViolationRuleCategory = (ruleId, category) =>
  api.patch(`${RULES_V2}/${ruleId}/category`, { category }).then((response) => response.data);

/**
 * Список правил для фильтра вкладки аналитики: ACL-aware, без прав
 * администратора. Scope — массивы factory_ids / … / camera_ids.
 * kind: 'violation' | 'business'.
 */
export const listAnalyticsRuleOptions = (kind, params = {}, { signal } = {}) =>
  api.get(kind === 'business' ? '/analytics/business-rules' : '/analytics/violation-rules', {
    params: Object.fromEntries(
      Object.entries(params).filter(([, value]) => (
        Array.isArray(value) ? value.length > 0 : value !== '' && value != null
      ))
    ),
    signal,
  }).then((response) => normalizeListResponse(response.data, 0));

/** Оба срока хранения задаются вместе: контракт запрещает половинчатую пару. */
export const updateViolationRuleRetention = (ruleId, payload) =>
  api.put(`${RULES_V2}/${ruleId}/retention`, payload).then((response) => response.data);

export const listEventEvidence = (eventId) =>
  api.get(`/violations/events/${eventId}/evidence`).then((response) => response.data);

/** Ссылка временная: хранить её как идентичность нельзя, идентичность — evidence_id. */
export const getEvidenceUrl = (evidenceId) =>
  api.get(`/violations/evidence/${evidenceId}/url`).then((response) => response.data);

/**
 * Evidence Addendum v1.0, раздел 4: главным считается role=primary.
 * Если primary нет — берём первый доступный, падать и рисовать broken-image
 * нельзя. Устаревший is_primary оставлен как запасной ключ.
 */
export const pickPrimaryEvidence = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    items.find((item) => item?.role === 'primary')
    || items.find((item) => item?.is_primary)
    || items.find((item) => item?.status === 'ready')
    || items[0]
  );
};

/** Форма одного Evidence в терминах UI. URL сюда не входит: он временный. */
export const normalizeEvidenceItem = (item = {}) => ({
  evidenceId: item.evidence_id ?? item.id ?? '',
  role: item.role ?? '',
  status: item.status ?? EVIDENCE_STATUS.PROCESSING,
  failureCode: item.failure_code ?? '',
  capturedAt: item.captured_at ?? '',
  expiresAt: item.expires_at ?? null,
});

/** Полный список Evidence события — карточка работает именно с ним. */
export const listEventEvidenceItems = (eventId) =>
  listEventEvidence(eventId).then((data) =>
    normalizeListResponse(data).items.map(normalizeEvidenceItem));

/**
 * Единственная точка, которой пользуется UI списка и карточки.
 *
 * Пока endpoint'ов ровно два, поэтому здесь два запроса. Когда backend научится
 * отдавать batch/enrichment, меняется только эта функция — компоненты не
 * трогаются (PART 3, раздел 3).
 */
export const getPrimaryEvidencePreview = (eventId) =>
  listEventEvidence(eventId).then((data) => {
    const { items } = normalizeListResponse(data);
    const primary = pickPrimaryEvidence(items);

    // Пустой список — нормальное «Фото отсутствует», а не ошибка (раздел 4).
    if (!primary) {
      return { evidenceId: '', status: EVIDENCE_STATUS.EMPTY, failureCode: '', url: '' };
    }

    const base = {
      evidenceId: primary.evidence_id ?? primary.id ?? '',
      status: primary.status ?? EVIDENCE_STATUS.PROCESSING,
      failureCode: primary.failure_code ?? '',
      url: '',
    };

    if (base.status !== EVIDENCE_STATUS.READY || !base.evidenceId) return base;

    return getEvidenceUrl(base.evidenceId)
      .then((link) => ({ ...base, url: link?.url ?? '' }))
      // Ссылка не выдалась — это не «фото сломано», а «пока недоступно».
      .catch(() => ({ ...base, status: EVIDENCE_STATUS.PROCESSING }));
  });

/**
 * FE-FIX15. POST /analytics/event-exclusions/bulk — единственный способ
 * пометить события как не учитываемые в аналитике.
 *
 * Ответ backend авторитетен: `changed` и `already_in_state` показывают, что
 * реально произошло, поэтому UI после вызова перечитывает список, а не
 * перекрашивает строки сам.
 */
export const bulkSetEventAnalyticsExclusion = (eventIds, excluded) => {
  const ids = (Array.isArray(eventIds) ? eventIds : [])
    .map(Number)
    .filter(Number.isFinite);

  if (ids.length === 0) {
    return Promise.resolve({ requested: 0, changed: 0, alreadyInState: 0, excluded });
  }

  return api
    .post('/analytics/event-exclusions/bulk', { event_ids: ids, excluded })
    .then((response) => ({
      requested: Number(response.data?.requested) || 0,
      changed: Number(response.data?.changed) || 0,
      alreadyInState: Number(response.data?.already_in_state) || 0,
      excluded: response.data?.excluded ?? excluded,
    }));
};

/**
 * POST /violations/events/count-selected. Запрос только читает (ничего не
 * меняет), POST нужен из-за размера тела. Тело строго `{event_ids}` или
 * `{filters}` — собирает buildCountSelectedPayload.
 */
export const countSelectedEvents = (body) =>
  api.post('/violations/events/count-selected', body).then((response) => response.data);
